import {
  tablesDB,
  DATABASE_ID,
  CART_TABLE_ID,
  STORE_TABLE_ID,
  USERS_TABLE_ID,
  Query,
} from '../lib/appwrite';

/**
 * Get all cart items for admin with lookup data (stores, users)
 * @param {Object} filters - { storeId, userId }
 */
export async function getCartsForAdmin(filters = {}) {
  const queries = [Query.orderDesc('createdAt'), Query.limit(2000)];
  if (filters.storeId) queries.push(Query.equal('storeId', filters.storeId));
  if (filters.userId) queries.push(Query.equal('userId', filters.userId));

  const [cartRes, storesRes] = await Promise.all([
    tablesDB.listRows(DATABASE_ID, CART_TABLE_ID, queries),
    tablesDB.listRows(DATABASE_ID, STORE_TABLE_ID, [Query.limit(500)]),
  ]);

  const items = cartRes.rows || [];
  const stores = storesRes.rows || [];
  const storeMap = stores.reduce((acc, s) => {
    acc[s.$id] = s.storeName || s.storeId || s.$id;
    return acc;
  }, {});

  const userIds = [...new Set(items.map((i) => i.userId).filter(Boolean))];
  const userPromises = userIds.slice(0, 200).map((id) =>
    tablesDB.getRow(DATABASE_ID, USERS_TABLE_ID, id).catch(() => ({
      $id: id,
      fname: 'Unknown',
      lname: '',
      email: null,
    }))
  );
  const userRows = await Promise.all(userPromises);
  const userMap = {};
  userRows.forEach((u) => {
    userMap[u.$id] = [u.fname, u.lname].filter(Boolean).join(' ') || u.email || u.$id;
  });

  return items.map((row) => {
    const qty = parseInt(row.quantity, 10) || 1;
    const price = parseFloat(row.price) || 0;
    const shipping = parseFloat(row.shippingFee) || 0;
    const lineTotal = qty * price + shipping;
    return {
      $id: row.$id,
      productId: row.productId,
      productName: row.productName,
      storeId: row.storeId,
      storeName: storeMap[row.storeId] || row.storeId,
      userId: row.userId,
      userName: userMap[row.userId] || row.userId,
      quantity: qty,
      unit: row.unit,
      packSize: row.packSize,
      price,
      shippingFee: shipping,
      shippingOption: row.shippingOption,
      lineTotal,
      createdAt: row.createdAt || row.$createdAt,
      image: row.image,
    };
  });
}

/**
 * Get filter options and cart summaries (grouped by user)
 * @returns {Object} { filters: { stores, users }, cartSummary, userMap, storeMap }
 */
export async function getCartFiltersAndSummary() {
  const [cartRes, storesRes] = await Promise.all([
    tablesDB.listRows(DATABASE_ID, CART_TABLE_ID, [Query.limit(5000)]),
    tablesDB.listRows(DATABASE_ID, STORE_TABLE_ID, [Query.limit(500)]),
  ]);

  const items = cartRes.rows || [];
  const stores = storesRes.rows || [];
  const storeMap = stores.reduce((acc, s) => {
    acc[s.$id] = s.storeName || s.storeId || s.$id;
    return acc;
  }, {});

  const userIds = [...new Set(items.map((i) => i.userId).filter(Boolean))];
  const userPromises = userIds.slice(0, 200).map((id) =>
    tablesDB.getRow(DATABASE_ID, USERS_TABLE_ID, id).catch(() => ({
      $id: id,
      fname: 'Unknown',
      lname: '',
      email: null,
    }))
  );
  const userRows = await Promise.all(userPromises);
  const userMap = {};
  userRows.forEach((u) => {
    userMap[u.$id] = [u.fname, u.lname].filter(Boolean).join(' ') || u.email || u.$id;
  });

  const storeIds = [...new Set(items.map((i) => i.storeId).filter(Boolean))];
  const userCarts = {};
  items.forEach((row) => {
    const uid = row.userId || 'unknown';
    if (!userCarts[uid]) {
      userCarts[uid] = { userId: uid, itemCount: 0, total: 0, lastUpdated: null };
    }
    const qty = parseInt(row.quantity, 10) || 1;
    const price = parseFloat(row.price) || 0;
    const shipping = parseFloat(row.shippingFee) || 0;
    userCarts[uid].itemCount += 1;
    userCarts[uid].total += qty * price + shipping;
    const dt = row.createdAt || row.$createdAt;
    if (dt && (!userCarts[uid].lastUpdated || new Date(dt) > new Date(userCarts[uid].lastUpdated))) {
      userCarts[uid].lastUpdated = dt;
    }
  });

  return {
    filters: {
      stores: storeIds.map((id) => ({ id, name: storeMap[id] || id })),
      users: Object.entries(userMap).map(([id, name]) => ({ id, name })),
    },
    cartSummary: Object.values(userCarts),
    userMap,
    storeMap,
  };
}

export async function deleteCartItem(rowId) {
  return tablesDB.deleteRow(DATABASE_ID, CART_TABLE_ID, rowId);
}
